import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { jwtDecode } from 'jwt-decode';

interface PermissionsContextType {
  modulos: string[];
  acciones: string[];
  hasModuleAccess: (ruta: string) => boolean;
  canAuthorize: (accion: string) => boolean;
  idSucursal: number | null;
  isPermissionsLoaded: boolean;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

interface PermissionsProviderProps {
  children: ReactNode;
}

interface DecodedToken {
  sub: number;
  username: string;
  rol: string;
  perfil: string;
  idRol: number;
  idPerfil: number;
  idSucursal: number;
  modulos: string[];
  acciones: string[];
  ip: string;
  exp: number;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const { token } = useAuth();
  const [modulos, setModulos] = useState<string[]>([]);
  const [acciones, setAcciones] = useState<string[]>([]);
  const [idSucursal, setIdSucursal] = useState<number | null>(null);
  const [isPermissionsLoaded, setIsPermissionsLoaded] = useState(false);

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode<DecodedToken>(token);
        setModulos(decoded.modulos || []);
        setAcciones(decoded.acciones || []);
        setIdSucursal(decoded.idSucursal || null);
      } catch (error) {
        console.error('Error al decodificar token:', error);
        setModulos([]);
        setAcciones([]);
        setIdSucursal(null);
      }
    } else {
      setModulos([]);
      setAcciones([]);
      setIdSucursal(null);
    }
    setIsPermissionsLoaded(true);
  }, [token]);

  const hasModuleAccess = (ruta: string): boolean => {
    return modulos.includes(ruta);
  };

  const canAuthorize = (accion: string): boolean => {
    return acciones.includes(accion);
  };

  return (
    <PermissionsContext.Provider 
      value={{
        modulos,
        acciones,
        hasModuleAccess,
        canAuthorize,
        idSucursal,
        isPermissionsLoaded,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions debe ser usado dentro de un PermissionsProvider');
  }
  return context;
};